import React from 'react';
import { CreditCard, Plus, Trash2, DollarSign, Calendar, TrendingUp, AlertCircle, Edit2, X } from 'lucide-react';
import { collection, addDoc, onSnapshot, Timestamp, deleteDoc, doc, query, where, getDocs, writeBatch, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { addEvent } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import { format, startOfMonth } from 'date-fns';

interface Subscription {
  id: string;
  userId: string;
  name: string;
  amount: number;
  dueDate: number;
  createdAt: Date;
}

export default function Subscriptions() {
  const [showForm, setShowForm] = React.useState(false);
  const [subscriptions, setSubscriptions] = React.useState<Subscription[]>([]);
  const { user } = useAuth();
  const [editingSubscription, setEditingSubscription] = React.useState<Subscription | null>(null);
  const [formData, setFormData] = React.useState({
    name: '',
    amount: '',
    dueDate: '1'
  });

  React.useEffect(() => {
    if (!user) return;

    const subscriptionsQuery = query(
      collection(db, 'subscriptions'),
      where('userId', '==', user.uid)
    );

    const unsubscribe = onSnapshot(subscriptionsQuery, (snapshot) => {
      const subsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      })) as Subscription[];
      
      setSubscriptions(subsList);
    });

    return () => unsubscribe();
  }, [user]);

  const createMonthlyPayment = async (subscription: Subscription, docRef: any) => {
    const today = new Date();
    const startDate = startOfMonth(today);
    const paymentDay = new Date(startDate.getFullYear(), startDate.getMonth(), subscription.dueDate);
    const recurringGroupId = crypto.randomUUID();

    // Create only one payment for the current month
    await addEvent({
      id: crypto.randomUUID(),
      title: `${subscription.name} Subscription`,
      amount: subscription.amount,
      type: 'bill',
      date: paymentDay,
      category: 'subscription',
      isRecurring: true,
      frequency: 'monthly',
      subscriptionId: docRef.id,
      recurringGroupId,
      isSubscription: true,
      isAutopay: true
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    
    try {
      const subscriptionData = {
        userId: user.uid,
        name: formData.name.trim(),
        amount: parseFloat(formData.amount),
        dueDate: parseInt(formData.dueDate),
        createdAt: Timestamp.now()
      };

      if (editingSubscription) {
        const subscriptionRef = doc(db, 'subscriptions', editingSubscription.id);
        await updateDoc(subscriptionRef, {
          ...subscriptionData,
          updatedAt: Timestamp.now()
        });

        // Update existing calendar events
        const eventsRef = collection(db, 'events');
        const futureEvents = await getDocs(
          query(eventsRef, 
            where('subscriptionId', '==', editingSubscription.id),
            where('date', '>=', new Date())
          )
        );

        const batch = writeBatch(db);
        futureEvents.docs.forEach(doc => {
          batch.update(doc.ref, {
            title: `${subscriptionData.name} Subscription`,
            amount: subscriptionData.amount,
            updatedAt: Timestamp.now()
          });
        });

        await batch.commit();
      } else {
        const docRef = await addDoc(collection(db, 'subscriptions'), subscriptionData);
        await createMonthlyPayment(subscriptionData, docRef);
      }

      setFormData({
        name: '',
        amount: '',
        dueDate: '1'
      });
      setShowForm(false);
      setEditingSubscription(null);
    } catch (error) {
      console.error('Error saving subscription:', error);
      alert('Failed to save subscription. Please try again.');
    }
  };

  const handleDeleteSubscription = async (id: string) => {
    if (confirm('Are you sure you want to delete this subscription? This will also remove all associated calendar bills.')) {
      try {
        await deleteDoc(doc(db, 'subscriptions', id));
        
        const eventsRef = collection(db, 'events');
        const snapshot = await getDocs(query(eventsRef, where('subscriptionId', '==', id)));
        
        const batch = writeBatch(db);
        snapshot.docs.forEach((doc) => {
          batch.delete(doc.ref);
        });
        await batch.commit();
      } catch (error) {
        console.error('Error deleting subscription:', error);
        alert('Failed to delete subscription. Please try again.');
      }
    }
  };

  const totalMonthly = subscriptions.reduce((sum, sub) => sum + sub.amount, 0);
  const totalYearly = totalMonthly * 12;

  if (!user) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Authentication Required</h3>
          <p className="mt-1 text-sm text-gray-500">Please sign in to access your subscriptions.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent flex items-center">
            <CreditCard className="w-6 h-6 mr-2" />
            Subscriptions Manager
          </h2>
          <button
            onClick={() => {
              setEditingSubscription(null);
              setFormData({
                name: '',
                amount: '',
                dueDate: '1'
              });
              setShowForm(!showForm);
            }}
            className="flex items-center px-4 py-2 gradient-primary text-white rounded-lg hover:scale-105 transition-all duration-300 shadow-lg cyber-glow"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Subscription
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Monthly Total</h3>
              <DollarSign className="w-8 h-8 opacity-75" />
            </div>
            <p className="text-3xl font-bold">${totalMonthly.toFixed(2)}</p>
            <p className="text-indigo-100 mt-2">{subscriptions.length} active subscriptions</p>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Yearly Total</h3>
              <TrendingUp className="w-8 h-8 opacity-75" />
            </div>
            <p className="text-3xl font-bold">${totalYearly.toFixed(2)}</p>
            <p className="text-purple-100 mt-2">Projected annual spending</p>
          </div>
        </div>

        {showForm && (
          <div className="mb-8 bg-white p-6 rounded-lg shadow-lg border border-indigo-100">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingSubscription ? 'Edit Subscription' : 'Add New Subscription'}
              </h3>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingSubscription(null);
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Subscription Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 transition-colors"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Monthly Amount</label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      className="block w-full pl-7 rounded-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Due Date</label>
                  <select
                    value={formData.dueDate}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                    required
                  >
                    {[...Array(31)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {i + 1}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingSubscription(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors"
                >
                  {editingSubscription ? 'Update Subscription' : 'Add Subscription'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {subscriptions.map((subscription) => (
            <div
              key={subscription.id}
              className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 hover:border-indigo-200 transition-colors group"
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">
                      {subscription.name}
                    </h3>
                    <div className="flex items-center mt-1 text-sm text-gray-500">
                      <Calendar className="w-4 h-4 mr-1" />
                      Due on day {subscription.dueDate}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setEditingSubscription(subscription);
                        setFormData({
                          name: subscription.name,
                          amount: subscription.amount.toString(),
                          dueDate: subscription.dueDate.toString()
                        });
                        setShowForm(true);
                      }}
                      className="text-gray-400 hover:text-indigo-600 transition-colors p-1"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteSubscription(subscription.id)}
                      className="text-gray-400 hover:text-red-600 transition-colors p-1"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                  <div className="text-sm text-gray-500">Monthly</div>
                  <div className="text-lg font-semibold text-indigo-600">
                    ${subscription.amount.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {subscriptions.length === 0 && !showForm && (
            <div className="col-span-full text-center py-12 bg-white rounded-lg border border-gray-200">
              <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No subscriptions</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by adding your first subscription.</p>
              <div className="mt-6">
                <button
                  onClick={() => setShowForm(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Subscription
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}